import { PrimaryButton } from '@cb/apricot-react';
import { CircularProgress, Grid } from '@mui/material';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { Typography } from 'src/components';
import StopCircle from '@mui/icons-material/StopCircle';

interface SchoolHeaderProps {
  printing: boolean;
  onPrintPDF: VoidFunction;
}

const SchoolHeader: FC<SchoolHeaderProps> = ({ printing, onPrintPDF }) => {
  const { watch } = useFormContext();

  const processStatus = watch('processStatus');
  const appealEligibleUpdates = watch('appealEligibleUpdates');
  const submitter = watch('submitter');

  const formStatus = useMemo(() => {
    switch (processStatus) {
      case 'submit':
      case 'submitted':
        return (
          <div>
            Status: Submitted <br />
            (Application received but awaiting review)
          </div>
        );

      case 'processing':
        return 'Status: Under Review by ETS';

      case 'reopen':
        return (
          <div>
            Status: Requires more information <br />
            (Application review is on hold pending submission of outstanding information requested)
          </div>
        );

      case 'rejected':
        return (
          <div>
            Status: Rejected <br />
            (Unable to assign a College Board Code for application reviewed)
          </div>
        );

      case 'completed':
        return (
          <div>
            Status: Completed <br />
            (Code assigned or Updated to Level II)
          </div>
        );

      case 'started':
        return 'Status: Started';

      case 'appeal':
        return (
          <div>
            Status: Pending Appeal <br />
            (Additional review is required by this team for approval)
          </div>
        );
      case 'international':
        return (
          <div>
            Status: Pending International <br />
            (Additional review is required by this team for approval)
          </div>
        );
      case 'testSecurity':
        return (
          <div>
            Status: Pending Test Security <br />
            (Additional review is required by this team for approval)
          </div>
        );

      case 'opsApproved':
        return (
          <div>
            Status: Vetting Approved <br />
          </div>
        );

      default:
    }
  }, [processStatus]);

  const formHeader = useMemo(() => {
    switch (processStatus) {
      case 'submit':
      case 'submitted':
      case 'reopen':
        return <div>This request can be changed.</div>;
      case 'rejected':
        return appealEligibleUpdates ? (
          <>
            {appealEligibleUpdates === 'true' && <div>This request can be changed.</div>}
            {appealEligibleUpdates === 'false' && <div>This submitted request cannot be changed.</div>}
          </>
        ) : (
          <div>This submitted request can be changed.</div>
        );
      case 'processing':
      case 'completed':
      case 'appeal':
      case 'international':
      case 'testSecurity':
      case 'opsApproved':
        return <div>This submitted request cannot be changed.</div>;
      case 'started':
      default:
    }
  }, [appealEligibleUpdates, processStatus]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <div>
          <Typography variant="body1" style={{ fontWeight: 700, color: "#ff0000" }}>
            <StopCircle style={{ color: "red", verticalAlign: "middle" }} /> College Board School Code requests must be submitted by the head of school or district or a school/district employee with written authorization to submit on behalf of the head of school or district. Written authorization must be included with the submission to continue processing.
          </Typography>
          <br/>
        </div>
      </Grid>
      <Grid item xs={3} sx={{ borderRight: `1px solid black` }}>
        <Typography variant="h5">{formStatus}</Typography>
      </Grid>

      <Grid item xs={3} sx={{ borderRight: `1px solid black` }}>
        {submitter && (
          <Typography align="center" variant="h5">
            Submitter: {submitter}
          </Typography>
        )}
      </Grid>

      <Grid item xs={3} sx={{ borderRight: `1px solid black` }}>
        <Typography align="center" variant="h6">
          {formHeader}
        </Typography>
      </Grid>

      <Grid item xs={3}>
        <Grid container justifyContent="center">
          <PrimaryButton disabled={printing} onClick={onPrintPDF}>
            Print PDF
            {printing && <CircularProgress size="small" />}
          </PrimaryButton>
        </Grid>
      </Grid>
    </Grid>

  );
};

export default SchoolHeader;
